'use client';

import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { ComponentProps, ReactNode, createContext, useContext, useRef, useState } from 'react';
import { UseFormReturn, useForm, useWatch } from 'react-hook-form';
import { UserAccount } from '@/features/account/ui/user-account';
import { useTelephoneVerification } from '@/features/auth/lib/useTelephoneVerification';
import { QueryApi } from '@/features/query/api';
import { EventService } from '@/shared/lib/event-service';
import { isTelephoneValid } from '@/shared/lib/validations';
import { Country } from '@/shared/model/Country';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Form } from '@/shared/ui/form';
import { Icon } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';
import { useModalContext } from '@/shared/ui/modal/modal-context';
import { ModalProps } from '@/shared/ui/modal/types';
import { Popover } from '@/shared/ui/popover';
import './edit-account-details-modal.scss';

type FormValues = {
    name: string;
    surname: string;
    birthDate: string;
    phoneNumber: string;
    email: string;
    newsletter: boolean;
};

type Props = ModalProps & {
    children?: ReactNode;
};

type EditAccountDetailsModalContextValue = {
    form: UseFormReturn<FormValues>;
};

const EditAccountDetailsModalContext = createContext<EditAccountDetailsModalContextValue | null>(null);

const useEditAccountDetailsModalContext = () => {
    const context = useContext(EditAccountDetailsModalContext);
    if (!context) {
        throw new Error('EditAccountDetailsModal components must be used within EditAccountDetailsModal.');
    }
    return context;
};

const EditAccountDetailsModalProvider = ({ children }: Props) => {
    const customerQuery = useQuery(QueryApi.Customer.queries.Customer);

    const form = useForm<FormValues>({
        defaultValues: {
            name: customerQuery.data!.name,
            surname: customerQuery.data!.surname,
            birthDate: customerQuery.data!.birthDate,
            phoneNumber: `${customerQuery.data!.telephonePrefix} ${customerQuery.data!.telephone}`,
            email: customerQuery.data!.email,
            newsletter: customerQuery.data!.newsletter ?? false,
        },
    });

    return (
        <EditAccountDetailsModalContext.Provider
            value={{
                form,
            }}
        >
            {children}
        </EditAccountDetailsModalContext.Provider>
    );
};

const EditAccountDetailsModalTitle = () => {
    const t = useTranslations();
    return <>{t(`EDIT_ACCOUNT_DETAILS_MODAL.TITLE`)}</>;
};

type EditAccountDetailsModalFormProps = ComponentProps<typeof Layout>;
const EditAccountDetailsModalForm = ({ children, media, ...props }: EditAccountDetailsModalFormProps) => {
    const { form } = useEditAccountDetailsModalContext();
    return (
        <Layout element={Form} media={media} {...form} noValidate {...props}>
            {children}
        </Layout>
    );
};

const EditAccountDetailsModalUserAccount = () => {
    const t = useTranslations();
    const { form } = useEditAccountDetailsModalContext();
    const [name, surname] = useWatch({ control: form.control, name: ['name', 'surname'] });
    const customerQuery = useQuery(QueryApi.Customer.queries.Customer);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const uploadMutation = useMutation(QueryApi.Customer.mutations.UploadAvatar);
    const removeMutation = useMutation(QueryApi.Customer.mutations.RemoveAvatar);

    return (
        <UserAccount
            user={{
                firstName: name,
                lastName: surname,
                isPremiumMember: false,
                isVerified: false,
                profilePictureUrl: customerQuery.data?.avatarUrl,
            }}
            isLoading={uploadMutation.isPending || removeMutation.isPending}
            inputRef={inputRef}
            onClickAvatar={() => inputRef.current?.click()}
            onRemoveAvatar={() => removeMutation.mutateAsync()}
            onFileChange={async (event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (!file) return;
                try {
                    await uploadMutation.mutateAsync(file);
                } catch {
                    EventService.trigger('notifications:show', {
                        type: 'error',
                        title: t('USER_ACCOUNT.UPLOAD_AVATAR_FAILED'),
                    });
                }
            }}
        />
    );
};

type EditAccountDetailsModalFirstNameFieldProps = ComponentProps<typeof Layout>;
const EditAccountDetailsModalFirstNameField = ({
    media,
    ...props
}: EditAccountDetailsModalFirstNameFieldProps) => {
    const t = useTranslations();
    return (
        <Layout element="div" media={media} {...props}>
            <Form.TextField
                name="name"
                label={t('EDIT_ACCOUNT_DETAILS_MODAL.FIRST_NAME_LABEL')}
                rules={{ required: true }}
                data-testid="edit-account-details-first-name-field"
            />
        </Layout>
    );
};

type EditAccountDetailsModalLastNameFieldProps = ComponentProps<typeof Layout>;
const EditAccountDetailsModalLastNameField = ({
    media,
    ...props
}: EditAccountDetailsModalLastNameFieldProps) => {
    const t = useTranslations();
    return (
        <Layout element="div" media={media} {...props}>
            <Form.TextField
                name="surname"
                label={t('EDIT_ACCOUNT_DETAILS_MODAL.LAST_NAME_LABEL')}
                rules={{ required: true }}
                data-testid="edit-account-details-last-name-field"
            />
        </Layout>
    );
};

type EditAccountDetailsModalBirthDateFieldProps = ComponentProps<typeof Layout>;
const EditAccountDetailsModalBirthDateField = ({
    media,
    ...props
}: EditAccountDetailsModalBirthDateFieldProps) => {
    const t = useTranslations();
    return (
        <Layout element="div" media={media} {...props}>
            <Form.DateField
                name="birthDate"
                label={t('EDIT_ACCOUNT_DETAILS_MODAL.DATE_OF_BIRTH_LABEL')}
                rules={{ required: true }}
                data-testid="edit-account-details-date-of-birth-field"
            />
        </Layout>
    );
};

type EditAccountDetailsModalPhoneNumberFieldProps = ComponentProps<typeof Layout>;
const EditAccountDetailsModalPhoneNumberField = ({
    media,
    ...props
}: EditAccountDetailsModalPhoneNumberFieldProps) => {
    const t = useTranslations();
    const { form } = useEditAccountDetailsModalContext();
    const phoneNumber = useWatch({ control: form.control, name: 'phoneNumber' });
    const { isVerified, isPhoneValid, nextRetryAt, startVerification } = useTelephoneVerification(
        phoneNumber ?? '',
    );
    const [lastVerifiedNumber, setLastVerifiedNumber] = useState(phoneNumber);
    const isPhoneVerified = lastVerifiedNumber === phoneNumber && isVerified;

    const handleVerifyPhone = async () => {
        const telephone = form.getValues('phoneNumber');
        const onVerify = () => {
            setLastVerifiedNumber(phoneNumber);
            form.clearErrors('phoneNumber');
        };
        if (nextRetryAt && new Date(nextRetryAt).getTime() > Date.now()) {
            EventService.trigger('phone-verification:start', {
                telephone,
                nextRetryAt,
                onVerify,
            });
            return;
        }
        const response = await startVerification.mutateAsync(telephone);
        EventService.trigger('phone-verification:start', {
            telephone,
            nextRetryAt: response.nextRetryAt,
            onVerify,
        });
    };

    return (
        <Layout element="div" media={media} {...props}>
            <Form.TelephoneField
                defaultCountry={Country.CZ.toLowerCase()}
                supportedCountries={[Country.CZ.toLowerCase()]}
                name="phoneNumber"
                label={t('EDIT_ACCOUNT_DETAILS_MODAL.PHONE_NUMBER_LABEL')}
                type="tel"
                rules={{
                    required: true,
                    validate: {
                        isValidTelephone: (value) =>
                            isTelephoneValid(value) ||
                            t('VALIDATIONS.IS_NOT_IN_VALID_FORMAT', {
                                name: t('WORDS.TELEPHONE'),
                            }),
                        isVerified: () =>
                            isPhoneVerified ||
                            t('VALIDATIONS.IS_NOT_VERIFIED_HE', { name: t('WORDS.TELEPHONE') }),
                    },
                }}
                data-testid="edit-account-details-phone-number-field"
                right={
                    <div className="edit-account-details-modal__phone-actions">
                        {isPhoneVerified ? (
                            <Badge
                                variant="success"
                                appearance="outline"
                                className="edit-account-details-modal__verified-badge"
                                data-testid="edit-account-phone-verified-badge"
                            >
                                <Icon name="check" />
                                {t('EDIT_ACCOUNT_DETAILS_MODAL.VERIFIED_BADGE')}
                            </Badge>
                        ) : isPhoneValid ? (
                            <Button
                                className="edit-account-details-modal__verify-button"
                                data-testid="edit-account-verify-phone-button"
                                size="small"
                                onClick={handleVerifyPhone}
                                disabled={startVerification.isPending}
                            >
                                {t('EDIT_ACCOUNT_DETAILS_MODAL.VERIFY_BUTTON')}
                            </Button>
                        ) : null}
                    </div>
                }
            />
            <Popover>
                <Popover.Trigger asChild>
                    <button
                        type="button"
                        className="edit-account-details-modal__help-text"
                        data-testid="edit-account-phone-verification-help"
                    >
                        {t('EDIT_ACCOUNT_DETAILS_MODAL.WHY_VERIFY_LINK')}
                    </button>
                </Popover.Trigger>
                <Popover.Content
                    className="edit-account-details-modal__popover"
                    side="right"
                    avoidCollisions={false}
                >
                    <Popover.Arrow />
                    <div className="edit-account-details-modal__popover-header">
                        <Icon name="phone" width={24} height={24} />
                        <b className="edit-account-details-modal__popover-title">
                            {t('EDIT_ACCOUNT_DETAILS_MODAL.VERIFICATION_POPOVER_TITLE')}
                        </b>
                    </div>
                    <p className="edit-account-details-modal__popover-text">
                        {t('EDIT_ACCOUNT_DETAILS_MODAL.VERIFICATION_POPOVER_TEXT')}
                    </p>
                </Popover.Content>
            </Popover>
        </Layout>
    );
};

type EditAccountDetailsModalEmailFieldProps = ComponentProps<typeof Layout>;
const EditAccountDetailsModalEmailField = ({ media, ...props }: EditAccountDetailsModalEmailFieldProps) => {
    return (
        <Layout element="div" media={media} {...props}>
            <Form.TextField
                name="email"
                label="E-mail"
                type="email"
                disabled
                data-testid="edit-account-details-email-field"
            />
        </Layout>
    );
};

type EditAccountDetailsModalNewsletterFieldProps = ComponentProps<typeof Layout>;
const EditAccountDetailsModalNewsletterField = ({
    media,
    ...props
}: EditAccountDetailsModalNewsletterFieldProps) => {
    const t = useTranslations();
    return (
        <Layout element="div" media={media} {...props}>
            <Form.Checkbox
                name="newsletter"
                label={t('EDIT_ACCOUNT_DETAILS_MODAL.NEWSLETTER_LABEL')}
                data-testid="edit-account-details-newsletter-checkbox"
            />
        </Layout>
    );
};

type EditAccountDetailsModalSaveButtonProps = ComponentProps<typeof Layout>;
const EditAccountDetailsModalSaveButton = ({ media, ...props }: EditAccountDetailsModalSaveButtonProps) => {
    const t = useTranslations();
    const { onClose } = useModalContext();
    const { form } = useEditAccountDetailsModalContext();

    const updateCustomerMutation = useMutation(QueryApi.Customer.mutations.Update);

    const handleSubmit = async ({ name, surname, birthDate, phoneNumber, newsletter }: FormValues) => {
        const [telephonePrefix, telephone] = phoneNumber.split(' ');
        await updateCustomerMutation.mutateAsync({
            name,
            surname,
            birthDate,
            telephonePrefix,
            telephone,
            newsletterApproved: newsletter,
        });
        onClose();
    };

    return (
        <Layout
            element={Button}
            media={media}
            data-testid="edit-account-details-modal-save-button"
            onClick={form.handleSubmit(handleSubmit)}
            {...props}
        >
            {t('WORDS.SAVE')}
        </Layout>
    );
};

export {
    EditAccountDetailsModalProvider as Provider,
    EditAccountDetailsModalTitle as Title,
    EditAccountDetailsModalForm as Form,
    EditAccountDetailsModalUserAccount as UserAccount,
    EditAccountDetailsModalFirstNameField as FirstNameField,
    EditAccountDetailsModalLastNameField as LastNameField,
    EditAccountDetailsModalBirthDateField as BirthDateField,
    EditAccountDetailsModalPhoneNumberField as PhoneNumberField,
    EditAccountDetailsModalEmailField as EmailField,
    EditAccountDetailsModalNewsletterField as NewsletterField,
    EditAccountDetailsModalSaveButton as SaveButton,
};
